import React, { FC, useEffect } from 'react'
import { withNavigation, WithNavigationProps } from 'hocs'
import { useLazyGetSentEmailsQuery } from 'orders/reduxQueries'
import { PageContent } from 'uiComponents/appLayouts/pageContent'
import { PageTitle } from 'uiComponents/typography'
import { useHasFeature } from 'features'
import './sentEmailsPage.scss'
import { PageLoader } from 'uiComponents/loaders'
import { Alert } from '@mui/material'
import { SerializedError } from '@reduxjs/toolkit'
import { NavigationBack } from 'uiComponents/navigation/navigationBack'
import { useHistory } from 'react-router-dom'
import { useTranslation } from 'translations'

interface SentEmailProps {
    timestamp: string
    type: string
    url: string
}

const SentEmail: FC<SentEmailProps> = ({ timestamp, type, url }) => (
    <div className="sent-email">
        <span>{timestamp} UTC&nbsp;</span>
        <a href={url} target="_blank" rel="noreferrer">
            {type}
        </a>
    </div>
)

interface SentEmailsPageProps extends WithNavigationProps<{ id: string; accountSlug: string }> {
    type: 'cart' | 'order'
}

const SentEmailsPage: FC<SentEmailsPageProps> = ({ match, type }) => {
    const { t } = useTranslation()
    const { accountSlug, id } = match.params
    const [getSentEmails, { data, isFetching, isError, error }] = useLazyGetSentEmailsQuery()
    const canSeeSentEmails = useHasFeature('show_sent_emails')
    const history = useHistory()

    useEffect(() => {
        canSeeSentEmails && getSentEmails({ id, accountSlug, type })
    }, [accountSlug, id, type, getSentEmails])

    if (isFetching) return <PageLoader />

    if (!canSeeSentEmails) {
        return null
    }

    return (
        <PageContent className="sent-emails-page">
            <PageTitle>{t('sentEmails.title')}</PageTitle>
            <NavigationBack
                onClick={() => history.replace(`/account/${accountSlug}/orders/${id}/details`)}
                text={t('sentEmails.backToOrder')}
            />
            {isError || !data ? (
                <Alert severity="error" className="sent-emails-page-error">
                    {(error as SerializedError)?.message || t('sentEmails.loadError')}
                </Alert>
            ) : (
                <pre>
                    {Object.keys(data).length > 0 ? (
                        Object.entries(data)
                            .sort(([a], [b]) => b.localeCompare(a))
                            .map(([key, values]) =>
                                values.map((value) => (
                                    <SentEmail key={key} timestamp={key} type={value.email_type} url={value.url} />
                                )),
                            )
                    ) : (
                        <div>{t('sentEmails.emptyState')}</div>
                    )}
                </pre>
            )}
        </PageContent>
    )
}

export default withNavigation(SentEmailsPage)
